// The relay's REST API (RFC-043 "Web client"). One origin; the browser's
// identity is the relay's cookie, so every call is same-origin with
// credentials and nothing here holds a token.
import { decodeEvent, type Caps, type EventBody, type Masked, type Policy, type Score, type SessionInfo, type Source, type AgentInfo } from "./protocol";
import { t, word } from "./i18n";

export type Role = "owner" | "coach" | "spectator" | "caller";

/** A person's role in a recording (RFC-043 "Recordings"). */
export type RecordingRole = "owner" | "reviewer" | "instructor";

export interface User {
  id: string;
  email: string;
  name: string;
  /** An admin, by the relay's configuration (RFC-050 "By grant"), or delegated for a time: /admin is theirs. */
  admin?: boolean;
  /** For an admin by delegation, when it ends (byj-1wv.72). */
  admin_until?: string;
  /** The add-ons they hold now, such as "machines". */
  addons?: string[];
  /** Their place in the item bank, when let in on a Share with an exam server (RFC-056). */
  bank?: { role: "admin" | "user"; url: string };
  /** The main server's modules that are on (RFC-059 §6), as its profile has them; none said, every one. */
  modules?: string[];
  /** The languages its profile offers, the first the default; none said, every one (byj-1wv.114.13). */
  locales?: string[];
  /** Its profile's theme; none said, the app's own. */
  theme?: string;
  /**
   * What they are to the third party's system that sent them here (byj-1wv.117.10): a candidate,
   * an author its author link made one (RFC-063), or an operator where the profile signs in by
   * entry links alone. Shown the exam, their authoring or the console, not Share's own places and
   * actions; none, everyone else.
   */
  integration?: "candidate" | "operator" | "author";
  /** The profile's UI edition a candidate or an operator wears (RFC-062): its own pages; none, Share's. */
  ui?: string;
  /** The product's name and mark its edition draws (RFC-062 D8). */
  product?: Product;
  /** Where they leave to when they sign out: the third party's system their link came from (RFC-062 D7). */
  exit_url?: string;
}

/** What a profile's edition calls the product, and its mark's URL (RFC-062 D8). */
export interface Product {
  name: string;
  mark?: string;
}

/** How a person may sign in, and the app's languages and theme, as the main server's profile has them (byj-1wv.114.13). */
export interface SignIn {
  /** The login providers it offers (byj-1wv.1.12): the login page offers those and no other. */
  providers: ("google" | "github")[];
  /** Whether a magic link by email signs one in; true when it does not say. */
  email: boolean;
  locales?: string[];
  theme?: string;
  /** The profile's UI edition, its product, and where one signed out of it signs in again (RFC-062). */
  ui?: string;
  product?: Product;
  signin_url?: string;
}

export interface Device {
  id: string;
  name: string;
  last_seen: string;
  sidecar_version: string;
}

export interface ShareSummary {
  id: string;
  title: string;
  source: Source;
  owner: { id: string; name: string };
  role: Role;
  started_at: string;
  participants: number;
  live: boolean;
  /** The group it is in (RFC-044). */
  group?: string;
  /** Which session it is — kept by the relay once the share has ended, so its owner can resume it (byj-1wv.12). */
  session?: SessionInfo;
  /** The class it was shared to, and the assignment it works on (RFC-043 model 19). */
  class_id?: string;
  assignment?: AssignmentRef;
  /** What a live share can do, from its sidecar's open; absent once it has ended. */
  caps?: Caps;
  /** A live A2A agent share's card (RFC-043 model 20). */
  agent?: AgentInfo;
  /** When it ended; neither this nor `live`, its sidecar is gone and its owner may reconnect it (byj-fym). */
  ended_at?: string;
  /** An ended share's turns and cost, as its sidecar last reported them; absent when none (byj-fym). */
  turns?: number;
  cost_usd?: number;
  /** A live share's state (working, needs-input, idle, paused) and the permissions it waits on, from its latest status (byj-fym). */
  state?: string;
  pending_permissions?: number;
}

/** Several of one owner's shares under one invite and one page (RFC-044 "Groups"). */
export interface Group {
  id: string;
  title: string;
  owner: { id: string; name: string };
  role: Role;
  url: string;
  /** The group's shares this person takes part in. */
  shares: ShareSummary[];
}

export interface ShareDetail {
  id: string;
  title: string;
  source: Source;
  owner: { id: string; name: string };
  role: Role;
  policy: Policy;
  live: boolean;
  screen: boolean;
  /** When it started: the owner's bar says how long it has run. */
  started_at?: string;
  /** Shared to a class (RFC-043 M3): a recording of it keeps the class's retention. */
  class?: boolean;
  class_id?: string;
  /** I am its class's instructor, not a coach its owner invited (byj-1wv.51.6). */
  instructor?: boolean;
  /** The class assignment it works on (RFC-043 model 19). */
  assignment?: AssignmentRef;
  /** What its session can carry, while it is open (RFC-044); see `capsOf`. */
  caps?: Caps;
  session?: SessionInfo;
  /** The highest seq the relay's backlog has let go, when it has let any: what came before is not shown (byj-1wv.20). */
  backlog_floor?: number;
  /** An agent share's card (RFC-043 model 20). */
  agent?: AgentInfo;
  /** The owner's view: recordings made of this share (byj-1wv.5.8). */
  recordings?: ShareRecording[];
}

/** A bearer token for an agent share's A2A endpoint (RFC-043 model 20). */
export interface AgentToken {
  token: string;
  expires_at: string;
  url: string;
  card_url: string;
}

/** A recording made of a share, as its page links it. */
export interface ShareRecording {
  id: string;
  review_url: string;
  turns: [number, number];
  event_count: number;
  uploaded: boolean;
  created_at: string;
}

export interface Invite {
  /** what its owner lists and revokes it by (byj-60g); a recording's has none */
  id?: string;
  url: string;
  expires_at: string;
}

/** What an invite is to, before it is accepted (byj-1wv.119). */
export interface InviteInfo {
  role: string;
  /** an agent share's */
  agent?: boolean;
  /** the agent share keeps what its callers ask and its answers, for its owner to read */
  keeps_questions?: boolean;
}

/** What an owner's open invites are to: a share's (byj-60g), a template's or a file's (byj-1wv.76). */
export type InviteTarget = { kind: "shares" | "app-templates" | "files"; id: string };

const invitesOf = (to: InviteTarget) => `/v1/${to.kind}/${enc(to.id)}/invites`;

/** An invite that still admits someone, as its owner sees it: never its link (byj-60g). */
export interface OpenInvite {
  id: string;
  role: string;
  /** 0 for anyone with the link */
  max_uses: number;
  used: number;
  expires_at: string;
  created_at: string;
}

// ---- exams (RFC-048, byj-1wv.56.4/.5) -------------------------------------------

/** A question as the kit publishes it. */
export interface ExamQuestion {
  id: string;
  mode: "vibe" | "agent-build" | "choice";
  prompt: string;
  submit?: string[];
  /** a :choice question's options, numbered from 1, and how many to choose (RFC-055) */
  options?: string[];
  choose?: number;
  /** a question's own project and harness, when it brings them (RFC-055) */
  project?: string;
  harness?: string;
  criteria?: { agent?: string; "max-tool-calls"?: number; "max-cost-usd"?: number; "max-seconds"?: number; weight?: number };
}

/** What only the exam's instructor reads (the relay strips it for a student). */
export interface ExamInstructorPart {
  hidden?: string[];
  validation?: {
    "checked-at"?: number;
    checks?: { id: string; "ok?": boolean }[];
    harness?: { kind: string; agents?: string[]; built?: string[]; "ok?"?: boolean }[];
  };
  calibration?: {
    experiment?: string;
    questions?: { id: string; proposed?: Record<string, number>; why?: string }[];
  };
}

/** What a published kit says of itself. */
export interface ExamMeta {
  id: string;
  title: string;
  version: number;
  agents: string[];
  time_limit_min?: number;
  attempts?: number;
  questions: ExamQuestion[];
  public_hash: string;
  kit_hash: string;
  rubric?: {
    accuracy?: number;
    agent?: number;
    efficiency?: Record<string, number>;
    process?: number;
    "zero-at"?: number;
    /** RFC-055: the fluency rubric — the outcome, then the four competencies; efficiency reported only */
    profile?: "fluency";
    outcome?: number;
    competencies?: Record<string, { points?: number }>;
    report?: string[];
  };
  harness?: Record<string, string[]>;
  instructor?: ExamInstructorPart;
}

/** An exam's public part as edited on the page (byj-1wv.56.8). */
export interface ExamDraftBody {
  title: string;
  agents: string[];
  time_limit_min?: number;
  attempts?: number;
  rubric?: ExamMeta["rubric"];
  questions: ExamQuestion[];
}

/** What the instructor's machine said when it last laid the draft over the kit. */
export interface ExamDraftStatus {
  pulled_at: number;
  draft_updated_at: string;
  changes: string[];
  ok: boolean;
  checks: { id: string; ok: boolean }[];
  problems: string[];
  published_version?: number;
}

export interface ExamDraft {
  body: ExamDraftBody;
  updated_at: string;
  status?: ExamDraftStatus;
  status_at?: string;
}

export interface ExamSummary {
  id: string;
  title: string;
  created_at: string;
  versions: number;
  assignments: number;
  queued: number;
  /** A paper of the organisation's item bank (RFC-056): its versions are the bank's, it grades it. */
  bank?: { kind: "bank"; paper: string; request?: string; server?: string };
}

export interface ExamAssignmentView {
  id: string;
  exam: { id: string; title: string };
  version: number;
  class: { id: string; name: string; join_code: string };
  opens_at?: string;
  closes_at?: string;
  created_at: string;
  /** its review, and whether its grades are finished (RFC-055) */
  review?: ReviewSettings;
  /** where a candidate is offered to go back to once done: the third party's own system, for an integration's session (RFC-061) */
  return_url?: string;
  /** on a student's Exams list: how many of their own hand-ins have a grade released (byj-1wv.135) */
  released?: number;
}

/** An assignment's review (RFC-055 phase 2): reviewed and finished before release, the pass mark, the share drawn at random, how far two reviewers may differ. */
export interface ReviewSettings {
  review: boolean;
  pass_mark: number;
  sample: number;
  review_gap: number;
  finalized_at?: string;
  finalized_by?: string;
}

/** A reviewer's verdict on a grade. */
export interface ExamReview {
  id: string;
  grade_id: string;
  reviewer: { id: string; name?: string; email?: string };
  score: number;
  parts?: Record<string, unknown>;
  reason: string;
  note?: string;
  created_at: string;
}

/** A hand-in as the review sees it: why it is reviewed and how far it has come. */
export interface ReviewItem {
  submission_id: string;
  grade_id?: string;
  student: { id: string; name?: string; email?: string };
  question: string;
  score?: number;
  final?: number;
  why: string[];
  state: string;
  reviews: ExamReview[];
  flags?: string[];
}

export interface ReviewState {
  settings: ReviewSettings;
  reviewers: { id: string; name?: string; email?: string }[];
  items: ReviewItem[];
  totals: { student: { id: string; name?: string; email?: string }; total: number; pass: boolean }[];
  checks: { ok: boolean; problems: string[] };
  role: "instructor" | "reviewer";
}

/** An item of an instructor's bank on the relay (RFC-055): its metadata and how much it has been exposed. */
export interface BankItem {
  exam_id: string;
  id: string;
  title: string;
  version: number;
  item: {
    family: string;
    competencies: Record<string, number>;
    difficulty?: { authored?: number; measured?: number; discrimination?: number };
    minutes?: number;
    status?: string;
    "variant-of"?: string;
  };
  /** classes: the join codes of the classes it was set to (byj-1wv.56.19). */
  exposure: { papers: number; assignments: number; sat: number; classes?: string[] };
  created_at: string;
}

/** A line of an assignment's audit trail. */
export interface GradeEvent {
  id: string;
  submission_id?: string;
  grade_id?: string;
  actor: { id: string; name?: string; email?: string };
  action: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  note?: string;
  created_at: string;
}

/** An exam assigned to a class, with how far the class has got (byj-1wv.56.15). */
export interface ClassExam extends ExamAssignmentView {
  started: number;
  handed_in: number;
  to_grade: number;
}

export interface ExamDetail {
  id: string;
  title: string;
  created_at: string;
  versions: { version: number; public_hash: string; kit_hash: string; uploaded: boolean; meta: ExamMeta; created_at: string }[];
  assignments: ExamAssignmentView[];
  queued: number;
  /** A paper of the organisation's item bank (RFC-056), and the classes it may be set to. */
  bank?: { kind: "bank"; paper: string; request?: string; server?: string };
  allowed_classes?: string[];
}

/** A grade: its detail is byj's whole grade for the instructor, a student's view of it for its student. */
export interface ExamGrade {
  id: string;
  score: number;
  override?: number;
  override_note?: string;
  released: boolean;
  regrade_of?: string;
  grader_version: number;
  detail?: GradeDetail;
  created_at: string;
}

export interface GradeMeasure {
  part: string;
  value?: number | null;
  budget?: number | null;
  points?: number | null;
  /** absent for a measure reported, not scored (the fluency rubric's) */
  of?: number;
}

/** A fluency rubric's signal (RFC-055): a rule's, or Prism's verdicts with the words they rest on. */
export interface FluencySignal {
  signal: string;
  by: string;
  value: number;
  met?: number;
  of?: number;
  detail?: string;
  quotes?: { quote: string; at?: number | null }[];
}

export interface FluencyCompetency {
  value: number | null;
  points: number;
  of: number;
  signals: FluencySignal[];
}

export interface GradeDetail {
  score?: number;
  parts?: {
    accuracy?: number;
    agent?: number;
    efficiency?: Record<string, number | null>;
    process?: number | null;
    outcome?: number;
    delegation?: number;
    description?: number;
    discernment?: number;
    diligence?: number;
  };
  flags?: string[];
  error?: string;
  reasons?: {
    verdicts?: { name: string; "pass?": boolean; detail?: string }[];
    trials?: { trial: string; "resolved?": boolean; score: number }[];
    measures?: GradeMeasure[];
    agent?: { wanted?: string | null; used?: string[]; "ok?"?: boolean };
    fluency?: Record<string, FluencyCompetency>;
    process?: {
      score?: number | null;
      why?: string | null;
      tasks?: { "task-id": string; title?: string; process?: number | null; error?: string; items?: Record<string, { verdict: string; quote?: string | null }> }[];
    };
  };
}

/** A student's line against a released grade, and the instructor's answer (byj-1wv.56.6). */
export interface ExamDispute {
  id: string;
  grade_id: string;
  text: string;
  answer?: string;
  answered_at?: string;
  created_at: string;
}

export interface ExamSubmission {
  id: string;
  attempt_id: string;
  n: number;
  bundle_hash: string;
  late: boolean;
  uploaded: boolean;
  submitted_at: string;
  grade?: ExamGrade;
  student?: { id: string; name: string; email: string };
  question?: string;
  agent?: string;
  exam?: string;
  recording_id?: string;
  disputes?: ExamDispute[];
  /** the reviews of its current grade, for the instructor and reviewers (RFC-055) */
  reviews?: ExamReview[];
}

export interface ExamAttempt {
  id: string;
  question: string;
  agent: string;
  started_at: string;
  recording_id?: string;
  submissions: ExamSubmission[];
  /** The class share it is sat in, and whether it is live (byj-1wv.56.9). */
  share_id?: string;
  share_live?: boolean;
  /** Whose, in the instructor's view. */
  student?: { id: string; name: string; email: string };
}

/** A student's exam sat as one sitting (RFC-058): one clock, its deadline the relay's. */
export interface ExamSitting {
  started_at: string;
  deadline?: string;
  agent?: string;
  /** past the deadline; past its grace, when no hand-in is taken */
  over: boolean;
  closed: boolean;
  /** when its student finished it before its deadline, by Hand in all and finish (byj-1wv.113): its deadline then */
  finished_at?: string;
  handed_in: number;
  questions: number;
  /** whose, in the instructor's view */
  student?: { id: string; name: string; email: string };
}

// ---- the exam console (RFC-062 D9) ------------------------------------------

/** A session's candidates by where they stand, and its hand-ins waiting to be graded. */
export interface ConsoleCounts {
  candidates: number;
  waiting: number;
  sitting: number;
  submitted: number;
  graded: number;
  to_grade: number;
}

/** A session's exam as the console heads it. */
export interface ConsoleAssignment {
  id: string;
  title: string;
  opens_at?: string;
  closes_at?: string;
  questions: number;
  time_limit_min?: number;
  return_url?: string;
}

export interface ConsoleSessionSummary {
  class: string;
  name: string;
  created_at: string;
  assignment: ConsoleAssignment;
  counts: ConsoleCounts;
}

export type ConsoleState = "waiting" | "sitting" | "submitted" | "graded";

/** A candidate of a session as the console draws them. */
export interface ConsoleCandidate {
  user: string;
  name: string;
  /** the third party's key for them */
  key?: string;
  state: ConsoleState;
  ends_at?: string;
  sitting?: ExamSitting | null;
  /** their machine's share for the exam, live while it runs */
  share?: string;
  live: boolean;
  hand_ins: number;
  graded: number;
  total?: number;
  pass?: boolean;
}

export interface ConsoleSession {
  class: string;
  name: string;
  assignment: ConsoleAssignment;
  candidates: ConsoleCandidate[];
  counts: ConsoleCounts;
  now: string;
}

export interface ExamAssignmentPage {
  assignment: ExamAssignmentView;
  role: "instructor" | "student" | "reviewer";
  meta: ExamMeta;
  attempts: ExamAttempt[];
  now: string;
  /** the student's sitting, once begun */
  sitting?: ExamSitting | null;
  /** every student's, for the staff */
  sittings?: ExamSitting[];
}

// ---- classes (RFC-043 M3, byj-1wv.4) ------------------------------------------

/** When the relay raises an alert on a student's session. */
export interface AlertThresholds {
  permission_wait_s: number;
  cost_spike_x: number;
  idle_min: number;
}

/** A class's policy: what joining it consents to, and its alert thresholds. */
export interface ClassPolicy {
  /** The instructor may request turns in a student's session. */
  inject: boolean;
  /** An allowed request runs without asking the student (consented at join). */
  consent_inject: boolean;
  retention_days: number;
  alerts: AlertThresholds;
}

export type ClassRole = "instructor" | "student";

export interface ClassSummary {
  id: string;
  name: string;
  role: ClassRole;
  /** Every member's: a student shares with it (byj-1wv.4.11). */
  join_code?: string;
  members: number;
}

export interface Consent {
  version: string;
  text: string;
}

export interface ClassMember {
  user: string;
  name: string;
  role: ClassRole;
  /** Empty or absent until the member has agreed. */
  consent_at?: string | null;
  consent_version?: string | null;
}

// ---- assignments (RFC-043 model 19, byj-1wv.4.12) -------------------------------

/** Work the instructor set the class: a project, a task, a todo. */
export interface Assignment {
  id: string;
  /** What a student types: `byj run --share class:CODE/KEY`. */
  key: string;
  title: string;
  text: string;
  due_at?: string | null;
  created_at: string;
  /** Set once archived: takes no new share, keeps what it has. */
  archived_at?: string | null;
}

/** How a share, a card and a recording name their assignment. */
export interface AssignmentRef {
  key: string;
  title: string;
}

/** One as the instructor writes it; the key defaults to a1, a2, … */
export interface AssignmentIn {
  key?: string;
  title: string;
  text?: string;
  due_at?: string | null;
}

export interface ClassDetail {
  id: string;
  name: string;
  role: ClassRole;
  join_code?: string;
  policy: ClassPolicy;
  consent: Consent;
  /** The instructor's only. */
  members?: ClassMember[];
  /** The open ones; the instructor sees the archived too. */
  assignments?: Assignment[];
  /** What its instructor posted to the class, newest first (byj-1wv.135). */
  announcements?: Announcement[];
}

/** What a class's instructor posts to it: kept on its page for its students (byj-1wv.135). */
export interface Announcement {
  id: string;
  text: string;
  author: { id: string; name: string };
  created_at: string;
}

export interface JoinPreview {
  class: { id: string; name: string };
  consent: Consent;
  policy: ClassPolicy;
  /** The open assignments, one share command each. */
  assignments?: Assignment[];
}

/** A student's recording of an assignment, on its page. */
export interface AssignmentRecording {
  id: string;
  title: string;
  turns: [number, number];
  uploaded: boolean;
  created_at: string;
  expires_at: string;
  review_url: string;
  /** The turns' task score, computed on the owner's machine (RFC-043 model 19 "Scores"). */
  score?: Score;
  /** Its screen is kept, as an asciicast (byj-1wv.5.7). */
  screen?: boolean;
}

/** One of a class's recordings (byj-j98): as its assignment's page lists it, with the assignment it is of and the student who made it. */
export interface ClassRecording extends AssignmentRecording {
  assignment: AssignmentRef & { archived_at?: string };
  student: { id: string; name: string };
}

/** Every recording a class's instructor reads in it, newest first, and how many of its students have not recorded each assignment, by key. */
export interface ClassRecordingList {
  recordings: ClassRecording[];
  not_recorded: Record<string, number>;
}

/** One student on an assignment's page. */
export interface AssignmentRow {
  student: { id: string; name: string };
  started: boolean;
  shares: ClassSession[];
  recordings: AssignmentRecording[];
  /** what they handed in under it (RFC-054 §5) */
  files?: AssignmentFile[];
  notes: number;
  injections: number;
  /** Their latest scored recording's score. */
  score?: Score;
}

/** A file a student handed in under an assignment, as its page lists it. */
export interface AssignmentFile {
  id: string;
  name: string;
  kind: "file" | "folder";
  latest: number;
  late: boolean;
  updated_at: string;
  /** its instructor's check of it (byj-1wv.136) */
  review?: HandInReview;
}

export interface AssignmentPage {
  assignment: Assignment;
  students: AssignmentRow[];
}

export type SessionState = "working" | "needs-input" | "idle" | "paused" | "ended";

export interface ClassAlert {
  kind: string;
  at: string;
  detail?: string;
  seqs?: number[] | null;
}

/** One student session on the instructor's dashboard. */
export interface ClassSession {
  share: string;
  student: { id: string; name: string };
  title: string;
  source: Source;
  live: boolean;
  state: SessionState;
  turns: number;
  cost_usd: number;
  pending_permissions: number;
  alerts: ClassAlert[];
  last_event_at: string;
  /** The assignment it works on, and whether it started after the due date (RFC-043 model 19). */
  assignment?: AssignmentRef | null;
  late?: boolean;
}

/** One person's outcome of a review request (byj-1wv.44). */
export interface ReviewRequestResult {
  email: string;
  /** Known to the relay: made a reviewer at once, mailed the review links. */
  registered: boolean;
  sent: boolean;
  error?: string;
}

/** What its owner changes of a recording: its name, how long it is kept (byj-1wv.43). */
export interface RecordingPatch {
  title?: string;
  retention_days?: number;
}

/**
 * A person asked by email to review a recording, as its owner sees them:
 * joined or done once they review it, by their email; failed when the mail
 * did not go; expired once the invite it sent is over; waiting until then.
 * Reviewing when they are among its reviewers already: a person the relay
 * knew is made one though their mail fails.
 */
export interface ReviewRequest {
  email: string;
  state: "waiting" | "expired" | "failed" | "joined" | "done";
  requested_at: string;
  reviewing?: boolean;
}

export interface Recording {
  id: string;
  title: string;
  source: Source;
  turns: [number, number];
  event_count: number;
  masked: Masked[];
  owner: { id: string; name: string };
  role: RecordingRole;
  uploaded: boolean;
  created_at: string;
  expires_at: string;
  /** How long it is kept, in days from when it was recorded (byj-1wv.43). */
  retention_days?: number;
  /** Its owner's: the most days it may be kept; absent for a class's recording. */
  max_retention_days?: number;
  /** Its owner's: a class's retention keeps it, which its students consented to. */
  class_retention?: boolean;
  review_url: string;
  /** Its screen is kept, as an asciicast (byj-1wv.5.7). */
  screen?: boolean;
  /** A reviewer's own "review done". */
  done_at?: string;
  /** The owner's view: who reviews, and who is done. */
  reviewers?: { id: string; name: string; done_at?: string }[];
  /** The owner's view: who was asked by email, and how far each has got (byj-xgt). */
  review_requests?: ReviewRequest[];
  /** A reviewer's view: when they were asked by email, and the note (byj-xgt). */
  requested_at?: string;
  request_note?: string;
  /** The class assignment it is of (RFC-043 model 19). */
  assignment?: AssignmentRef;
  /** The class it was made in — its assignment's, or its share's — when it was made in one. */
  class?: { id: string; name: string };
  /** The turns' task score, from the owner's machine (RFC-043 model 19 "Scores"). */
  score?: Score;
}

/** An annotation on a recording's event, or on a line of a diff in one. */
export interface Annotation {
  id: string;
  /** The event's index in the recording, from 0. */
  seq_ref: number;
  /** "event", or "diff:<file>:<line>". */
  anchor: string;
  body: string;
  parent_id?: string;
  resolved: boolean;
  author: { id: string; name: string };
  created_at: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** what the relay answered, parsed: a refusal's code and figures */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The owner's monthly cap across their agent shares, and this month's spend
 * against it (byj-1wv.46): a caller's message past it is refused on every
 * agent share of theirs until the month turns (UTC). `set` is false while
 * the relay's default applies.
 */
export type AgentCap = { cap_usd: number; spent_usd: number; since: string; default_usd: number; set: boolean };

/**
 * A Share-hosted machine (RFC-050): a container with byj, Claude Code and
 * Codex that shares itself as a terminal its owner types into. `share_id`
 * once it has shared itself; `stops_at` the latest it stops (its longest run
 * or the month's hours; idle, sooner).
 */
export type MachineView = {
  id: string;
  state: "starting" | "running" | "stopped";
  started_at: string;
  stops_at: string;
  share_id?: string;
  share_url?: string;
  /** The exam assignment it was started for, and the exam's title (byj-1wv.62.10). */
  exam?: string;
  exam_title?: string;
  /** An author's machine, where they make kits (RFC-063). */
  author?: boolean;
};

/** A paper an author submitted, as the bank keeps it (RFC-063): its check, the byj of each, and its client's answer. */
export interface AuthorPaper {
  id: string;
  state: "checking" | "valid" | "invalid";
  title: string;
  kit_hash: string;
  why?: string;
  questions?: number;
  author?: string;
  submitted_at?: string;
  author_byj?: string;
  server_byj?: string;
  accepted_at?: string;
  rejected_at?: string;
  reject_reason?: string;
}

/** An author's month at the model gateway (RFC-063 D12): its cap, spent and left; none without a gateway or a cap. */
export interface AuthorModel {
  gateway: boolean;
  cap_usd: number;
  spent_usd: number;
  left_usd: number;
  since: string;
}

/** A machine's device token for one's own computer, said once (RFC-063 D5). */
export interface OwnMachineToken {
  token: string;
  device: { id: string; name: string; machine?: boolean };
  image: string;
  relay: string;
}

/**
 * A machine on one's own computer (RFC-050, byj-1wv.62.23, .25): the
 * published image and the relay it dials, and those running now — the
 * person's live shares a machine opened.
 */
export type OwnMachines = {
  image: string;
  relay: string;
  machines: { share_id: string; share_url: string; title: string; started_at: string }[];
};

/**
 * A person's machines and how long they have run this month (UTC), within
 * their month; whether they may start one — machines are by grant during the
 * beta (RFC-050 "By grant") — and when their grant ends.
 */
export type MachinesView = {
  machines: MachineView[];
  month_seconds: number;
  month_cap_seconds: number;
  allowed?: boolean;
  not_allowed?: "not_granted" | "expired";
  grant_expires_at?: string;
  /** How many days after their last machine stops their home is kept, 0 for always (byj-1wv.62.19). */
  home_retain_days?: number;
  /** When their home is removed: absent while a machine runs on it, while it is kept always, before a stop, and once past (byj-1wv.51.15). */
  home_removes_at?: string;
};

// ---- plans and payments (RFC-065, byj-1wv.132.8) ------------------------------

/** A plan on sale: its price in KRW, VAT included, for a month or a year, and what it grants. */
export type Plan = {
  id: string;
  version?: number;
  family: string;
  name: string;
  currency: string;
  price: number;
  /** once: a credit pack (RFC-065 D3); term: a class's seat (D7) */
  period: "month" | "year" | "once" | "term";
  recurring: boolean;
  grants: { addon: string; limits?: { monthly_hours?: number; quota_mb?: number; credit_hours?: number } }[];
  active: boolean;
};

/** The plans, and whether anything is sold here yet (`configured`) and for real (`live`). */
export type PlansView = { plans: Plan[]; configured: boolean; live: boolean };

/** A person's plan for a period: `renews` when a card renews it, `cancel_at_end` once cancelled. */
export type Subscription = {
  id: string;
  plan: Plan;
  status: "active" | "past_due";
  period_start: string;
  period_end: string;
  renews: boolean;
  card?: string;
  cancel_at_end: boolean;
  /** a new price, told of, charged from its renewal on `from` (RFC-065 D11) */
  next?: { price: number; currency: string; from: string };
};

/** An order: what was bought, for how much, how it stands, and the provider's receipt once paid. */
export type BillingOrder = {
  id: string;
  plan: string;
  name: string;
  amount: number;
  currency: string;
  status: "pending" | "paid" | "failed" | "canceled";
  method?: string;
  receipt_url?: string;
  created_at: string;
  paid_at?: string;
};

/** A person's billing: their plan, if any, and their orders; the provider's browser key when one sells here. */
export type BillingView = {
  configured: boolean;
  provider?: string;
  client_key?: string;
  live?: boolean;
  subscription: Subscription | null;
  orders: BillingOrder[];
  /** the seats I bought for my classes, while their terms run */
  seats?: { order_id: string; class_id: string; class_name: string; seats: number; taken: number; ends_at: string }[];
  /** what is left of my credit packs, each until it lapses */
  credits?: { addon: string; seconds_left: number; seconds: number; expires_at: string }[];
};

/** An order made for the provider's window: a card registered for it (`card`) or a payment once (`payment`). */
export type Checkout = {
  order_id: string;
  kind: "card" | "payment";
  amount: number;
  currency: string;
  order_name: string;
  customer_key: string;
  client_key: string;
  provider: string;
};

/** What paying settled: the order, and the plan it opened. */
export type Settled = { order: BillingOrder; subscription?: Subscription; waiting?: "deposit" };

/** The admin's Billing tab: every open plan and the latest payments, each with whose it is. */
export type AdminBilling = {
  configured: boolean;
  live: boolean;
  subscriptions: (Subscription & { user_id: string; email: string })[];
  orders: (BillingOrder & { user_id: string; email: string })[];
};

/** Where a grant came from (RFC-065 D8). */
export type GrantSource = "admin" | "contract" | "plan" | "credits" | "integration";

/** Whether an admin grants, changes and revokes from this source: admin and contract, never billing's or an integration's. */
export const adminManages = (g: { source?: GrantSource }) => !g.source || g.source === "admin" || g.source === "contract";

/** An add-on granted to a person (RFC-050 "By grant"), as an admin sees it. */
export type Grant = {
  user_id: string;
  email: string;
  name: string;
  addon: string;
  /**
   * Where it came from (RFC-065 D8): an admin's, a contract's an admin
   * keeps, a plan's or credits' made by billing, an integration's. A
   * person holds one a source; the gate reads the most generous.
   */
  source?: GrantSource;
  /** machines: hours a month; exam-bank: the role in the bank (RFC-056) */
  limits: { monthly_hours?: number; role?: "admin" | "user" };
  /** The admin's email. */
  granted_by: string;
  granted_at: string;
  expires_at?: string;
  live: boolean;
  month_seconds: number;
  month_cap_seconds: number;
};

/**
 * A person as an admin finds them, with their machines grant if any; one
 * named only by a grant has not signed in yet (`signed_in` false).
 */
export type AdminUser = { id: string; email: string; name: string; admin: boolean; signed_in?: boolean; machines?: Grant; demo?: Grant };

/** A person made an admin for a time by a configured admin (byj-1wv.72). */
export interface AdminDelegate {
  user_id: string;
  email: string;
  name: string;
  /** the configured admin's email */
  granted_by: string;
  granted_at: string;
  expires_at: string;
  /** holding now: before its end, its granter still configured */
  live: boolean;
}

/** Who is an admin: those configured where Share is deployed, and those delegated. */
export interface AdminsList {
  configured: string[];
  delegates: AdminDelegate[];
  /** whether the caller, a configured admin, may delegate */
  can_delegate: boolean;
  max_days: number;
}

/**
 * A person's machine home as an admin sees it (byj-1wv.62.19): where it is,
 * when their last machine stopped, the days an admin set (absent for the
 * relay's, 0 for always) and when it is removed — absent while a machine
 * runs, while it is kept always, or before its last stop is known.
 */
/** One question an agent share was asked (byj-1wv.48): its words only where the share kept them; rating the asker's, 1, -1 or 0. */
export type AgentExchangeView = {
  seq: number;
  asker_id: string;
  asker_name: string;
  asker_kind: string;
  question?: string;
  answer?: string;
  cost_usd: number;
  at: string;
  rating: number;
};
/** What an agent share was asked, for its owner (byj-1wv.48). */
export type AgentUse = {
  questions: number;
  askers: number;
  cost_usd: number;
  up: number;
  down: number;
  kept: number;
  keeping: boolean;
  exchanges: AgentExchangeView[];
};

/** One reach's agent shares in the pilot's report (byj-1wv.49): opened, declined when asked to confirm, by how many owners; reach "" is agents whose byj could not say. */
export type ReachCount = { reach: string; opened: number; declined: number; owners: number };
export type AgentShareReport = { reaches: ReachCount[]; opened: number; declined: number; since?: string };

export type MachineHome = {
  user_id: string;
  email: string;
  name: string;
  home: string;
  last_stopped_at?: string;
  retain_days?: number;
  removes_at?: string;
  running: boolean;
  removing: boolean;
  /** An exam's home (byj-1wv.62.10): the assignment it is for and the exam's title; kept until its person's hand-ins are graded. */
  exam?: string;
  exam_title?: string;
};

/** What an admin grants: to an email (kept for one who has not signed in yet) or a user. */
export type GrantRequest = {
  email?: string;
  user_id?: string;
  addon?: string;
  /** admin when none; contract for one paid outside Share (RFC-065 D6) */
  source?: "admin" | "contract";
  limits?: { monthly_hours?: number; role?: "admin" | "user" };
  expires_at?: string;
};

// ---- app templates (RFC-052, byj-1wv.64.5) --------------------------------------

/**
 * An app template as listed (RFC-052 "API"): `latest` is its newest uploaded
 * version, 0 while none is; `via` says how one shared with me reached me —
 * "invite", or "class:CODE".
 */
export interface AppTemplate {
  id: string;
  name: string;
  title: string;
  description: string;
  owner: string;
  mine: boolean;
  latest: number;
  updated_at: string;
  via?: string;
  /** the agent its latest version carries (RFC-060 model 7) */
  agent?: string;
  /** what that agent is for, as its latest version says it: read before a copy is made */
  agent_description?: string;
}

/**
 * An agent a person made with the agent builder (RFC-060 D12), as the main
 * server keeps it while its machine is off: the agent share it is served by,
 * live and connected by the relay's word, what it cost, whether it answers
 * its owner alone (on their subscription, D15), the last test the page reported.
 */
export interface BuiltAgent {
  name: string;
  share_id?: string;
  /** Its owner has its share on: a share stopped on purpose keeps its id, and is not shared (byj-1wv.115.49). */
  shared: boolean;
  live: boolean;
  connected: boolean;
  owner_only?: boolean;
  cost_usd: number;
  test?: AgentTestSummary;
  updated_at: string;
}

/** What a test of an agent's examples came to. */
export interface AgentTestSummary {
  passed: number;
  of: number;
  at?: number;
}

/** An agent share's application (RFC-060 D16): a caller of its own. */
export interface ShareApp {
  id: string;
  name: string;
  made_at: string;
  last_used_at?: string;
}

/** An application just made, or a token minted again: shown this once. */
export interface ShareAppToken {
  app: ShareApp;
  token: string;
  expires_at: string;
  url: string;
  card_url: string;
}

/** A version of a template; one not uploaded yet is shown to its owner alone. */
export interface AppTemplateVersion {
  n: number;
  hash: string;
  bytes: number;
  uploaded: boolean;
  created_at: string;
}

/** What a template's manifest (`.brainyard/app-template.edn`) says, as its latest version carries it. */
export interface AppTemplateManifest {
  name?: string;
  title?: string;
  description?: string;
  substitute?: string[];
  include?: string[];
  exclude?: string[];
}

/** /t/{id}: a template, my standing toward it, its versions; who else may use it, for its owner. */
export interface AppTemplateDetail {
  template: AppTemplate;
  role: "owner" | "user" | "class";
  versions: AppTemplateVersion[];
  manifest: AppTemplateManifest | null;
  classes?: { id: string; name: string; code: string }[];
  users?: { id: string; name: string; email: string }[];
}

/**
 * A file on Share (RFC-054): a file, or a folder kept as one tar.gz. `latest`
 * is its newest uploaded version, 0 while none is, and `bytes` and `preview`
 * are that version's; `via` says how one shared with me reached me —
 * "invite", or "class:CODE".
 */
export interface ShareFile {
  id: string;
  name: string;
  kind: "file" | "folder";
  description: string;
  owner: string;
  mine: boolean;
  latest: number;
  bytes: number;
  preview: FilePreview;
  updated_at: string;
  via?: string;
  /** The class assignment it was handed in under (RFC-054 §5), and when it goes: its class's retention after its latest upload. */
  hand_in?: { class: string; code: string; name: string; key: string; title: string };
  expires_at?: string;
  /** Who else sees it, counted on its owner's own list alone (RFC-054 §4): its viewers, and the classes given it. */
  viewers?: number;
  classes?: number;
  /** A hand-in's check by its class's instructor, said to its owner and that instructor (byj-1wv.136). */
  review?: HandInReview;
}

/** A hand-in checked, or returned for changes, by its class's instructor, with a mark if they gave one, of the version they read (byj-1wv.136). */
export interface HandInReview {
  state: "checked" | "returned";
  mark?: string;
  version: number;
  by: { id: string; name: string };
  at: string;
}

/** How the page shows a version: what the relay found its bytes to be; "" is download only. */
export type FilePreview = "text" | "image" | "pdf" | "folder" | "";

export interface ShareFileVersion {
  n: number;
  bytes: number;
  hash: string;
  content_type: string;
  preview: FilePreview;
  uploaded: boolean;
  /** a hand-in's, uploaded after its assignment was due */
  late?: boolean;
  created_at: string;
}

/** /f/{id}: a file, my standing toward it, its versions; who else sees it, for its owner. */
export interface ShareFileDetail {
  file: ShareFile;
  role: "owner" | "viewer" | "class" | "instructor";
  versions: ShareFileVersion[];
  viewers?: { id: string; name: string; email: string }[];
  classes?: { id: string; name: string; code: string }[];
}

/** A comment on a line of a file's version — of a file in a folder by its path (RFC-054 §7). */
export interface FileComment {
  id: string;
  version: number;
  path?: string;
  line: number;
  author: { id: string; name: string };
  body: string;
  parent_id?: string;
  resolved: boolean;
  created_at: string;
}

/** What a person's files take, as an admin sees it; quota_mb is what an admin set, absent for the relay's. */
export interface FileUse {
  user_id: string;
  email: string;
  name: string;
  files: number;
  used: number;
  quota: number;
  quota_mb?: number;
  /** bytes sent of their files this UTC month, and last (byj-1wv.71.3) */
  downloaded: number;
  downloaded_last: number;
}

/** The files' storage as it holds, or as the relay's flags have it (RFC-054 §6): bytes and USD. */
export interface FileLimits {
  default_quota: number;
  /** 0 for no limit */
  total_limit: number;
  usd_per_gb_month: number;
  usd_per_gb_out: number;
}

/** The files' storage settings: what holds, the relay's, which an admin set, and who last did. */
export interface FileSettings {
  settings: FileLimits;
  relay: FileLimits;
  set: (keyof FileLimits)[];
  updated_by?: string;
  updated_at?: string;
}

/** A change to the file settings: MB and USD, null back to the relay's, one left out unchanged. */
export interface FileSettingsChange {
  default_quota_mb?: number | null;
  total_limit_mb?: number | null;
  usd_per_gb_month?: number | null;
  usd_per_gb_out?: number | null;
}

/** One entry of a folder's version. */
export interface FolderEntry {
  path: string;
  bytes: number;
  dir: boolean;
}

/** What a person added by address came to: a viewer at once (registered), or mailed an invite. */
export interface FilePersonResult {
  email: string;
  registered: boolean;
  sent: boolean;
  error?: string;
}

/** A file's bytes, as a person chose or dropped it. */
export function bytesOf(f: Blob): Promise<ArrayBuffer> {
  if (typeof f.arrayBuffer === "function") return f.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.onerror = () => reject(r.error);
    r.readAsArrayBuffer(f);
  });
}

/** The SHA-256 of bytes, in hex, as the relay checks an upload against. */
export async function sha256Hex(b: ArrayBuffer): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", b);
  return [...new Uint8Array(d)].map((x) => x.toString(16).padStart(2, "0")).join("");
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: "same-origin", headers: { Accept: "application/json" } };
  // The relay takes a state-changing request only as JSON (its CSRF guard),
  // so one with nothing to say still sends {}.
  if (body !== undefined || method !== "GET") {
    init.body = JSON.stringify(body ?? {});
    init.headers = { ...init.headers, "Content-Type": "application/json" };
  }
  const res = await fetch(path, init);
  const text = await res.text();
  return parse<T>(res, text);
}

/** What an HTTP status the relay answered means, in a few words. */
function statusWord(status: number): string {
  return word("errors:status", String(status)) ?? t("errors:status.other");
}

function parse<T>(res: Response, text: string): T {
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  if (!res.ok) {
    // the relay says why in English, with no code: shown as it is, after
    // what the status means in the language shown (errors.json)
    const msg =
      data && typeof data === "object" && typeof (data as { error?: unknown }).error === "string"
        ? t("errors:relayError", { what: statusWord(res.status), detail: (data as { error: string }).error })
        : t("errors:noAnswer", { status: res.status, statusText: res.statusText });
    throw new ApiError(res.status, msg, data);
  }
  return data as T;
}

const enc = encodeURIComponent;

export const api = {
  /** The signed-in user, or null when the relay answers 401. */
  async me(): Promise<User | null> {
    try {
      const me = await call<{
        user: User;
        admin?: boolean;
        admin_until?: string;
        addons?: string[];
        bank?: User["bank"];
        modules?: string[];
        locales?: string[];
        theme?: string;
        integration?: User["integration"];
        ui?: string;
        product?: Product;
        exit_url?: string;
      }>(
        "GET",
        "/v1/me",
      );
      return {
        ...me.user,
        admin: me.admin ?? false,
        addons: me.addons ?? [],
        ...(me.admin_until ? { admin_until: me.admin_until } : {}),
        ...(me.bank ? { bank: me.bank } : {}),
        ...(me.modules ? { modules: me.modules } : {}),
        ...(me.locales ? { locales: me.locales } : {}),
        ...(me.theme ? { theme: me.theme } : {}),
        ...(me.integration ? { integration: me.integration } : {}),
        ...(me.ui ? { ui: me.ui } : {}),
        ...(me.product ? { product: me.product } : {}),
        ...(me.exit_url ? { exit_url: me.exit_url } : {}),
      };
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  /** The dev relay answers with a link to follow instead of sending mail; any relay answers a demo account's address so (byj-1wv.84.11). */
  async magic(email: string): Promise<{ dev_link?: string; demo_link?: string }> {
    return (await call<{ dev_link?: string; demo_link?: string } | undefined>("POST", "/v1/auth/magic", { email })) ?? {};
  },
  oauthStart: (provider: "google" | "github") => `/v1/auth/oauth/${provider}/start`,
  /** How a person may sign in here, and the app's languages and theme, before anyone has (byj-1wv.114.13). */
  signIn: async (): Promise<SignIn> => {
    const s = await call<Partial<SignIn>>("GET", "/v1/auth/providers");
    return {
      providers: s?.providers ?? [],
      email: s?.email ?? true,
      ...(s?.locales ? { locales: s.locales } : {}),
      ...(s?.theme ? { theme: s.theme } : {}),
      ...(s?.ui ? { ui: s.ui } : {}),
      ...(s?.product ? { product: s.product } : {}),
      ...(s?.signin_url ? { signin_url: s.signin_url } : {}),
    };
  },
  /** Signed out, answered with where they leave to when their link or the deployment says (RFC-062 D7). */
  logout: () => call<{ exit_url?: string } | undefined>("POST", "/v1/auth/logout"),
  approveDevice: (user_code: string) => call<void>("POST", "/v1/device/approve", { user_code }),
  devices: () => call<Device[]>("GET", "/v1/devices"),
  agentCap: () => call<AgentCap>("GET", "/v1/me/agent-cap"),
  /** Above zero, at most the relay's maximum: raised or lowered, never removed. */
  setAgentCap: (usd: number) => call<AgentCap>("PUT", "/v1/me/agent-cap", { cap_usd: usd }),
  revokeDevice: (id: string) => call<void>("DELETE", `/v1/devices/${enc(id)}`),
  shares: () => call<ShareSummary[]>("GET", "/v1/shares"),
  /** Its owner names it (byj-1wv.38): kept over its sidecar's title. */
  updateShare: (id: string, patch: { title: string }) => call<ShareSummary>("PATCH", `/v1/shares/${enc(id)}`, patch),
  /** Its owner names it (byj-1wv.38). */
  updateRecording: (id: string, patch: RecordingPatch) => call<Recording>("PATCH", `/v1/recordings/${enc(id)}`, patch),
  /** Its owner deletes an ended share; anyone else leaves it (byj-1wv.34). */
  deleteShare: (id: string) => call<void>("DELETE", `/v1/shares/${enc(id)}`),
  share: (id: string) => call<ShareDetail>("GET", `/v1/shares/${enc(id)}`),
  createInvite: (id: string, role: "coach" | "spectator" | "caller", max_uses: number, ttl_s: number) =>
    call<Invite>("POST", `/v1/shares/${enc(id)}/invites`, { role, max_uses, ttl_s }),
  /** Its owner's open invites to a share, a template or a file, newest first; revoking one stops its link (byj-60g, byj-1wv.76). */
  openInvites: (to: InviteTarget) => call<OpenInvite[]>("GET", invitesOf(to)),
  revokeInvite: (to: InviteTarget, invite: string) => call<void>("DELETE", `${invitesOf(to)}/${enc(invite)}`),
  /** A new link for what an open invite has left, the old one stopped in the same step. */
  replaceInvite: (to: InviteTarget, invite: string) =>
    call<Invite & { id: string; role: string; max_uses: number }>("POST", `${invitesOf(to)}/${enc(invite)}/replace`),
  /** A share's invite answers {share, role}; a recording's {recording, role}; a group's {group, role}. */
  /** A token for the agent share's A2A endpoint, for a participant who may ask (RFC-043 model 20). */
  agentToken: (id: string) => call<AgentToken>("POST", `/v1/shares/${enc(id)}/agent-token`, {}),
  /**
   * What an invite is to, read before it is accepted and spending nothing: an agent share's says whether
   * the share keeps its callers' questions, which the invite page asks the person to agree to first (byj-1wv.119).
   */
  inviteInfo: (token: string) => call<InviteInfo>("GET", `/v1/invites/${enc(token)}`),
  /** A template's invite answers {template, role} (RFC-052); a file's {file, role} (RFC-054). */
  acceptInvite: (token: string) =>
    call<{ share?: string; recording?: string; group?: string; template?: string; file?: string; role: Role | RecordingRole | "user" | "viewer" }>(
      "POST",
      `/v1/invites/${enc(token)}/accept`,
    ),
  group: (id: string) => call<Group>("GET", `/v1/groups/${enc(id)}`),

  recordings: () => call<Recording[]>("GET", "/v1/recordings"),
  recording: (id: string) => call<Recording>("GET", `/v1/recordings/${enc(id)}`),
  /** The recording's events, each line checked as Go's DecodeEvent checks it. */
  async recordingEvents(id: string): Promise<EventBody[]> {
    const res = await fetch(`/v1/recordings/${enc(id)}/events`, { credentials: "same-origin" });
    const text = await res.text();
    if (!res.ok) parse<unknown>(res, text);
    return text
      .split("\n")
      .filter((l) => l.trim() !== "")
      .map(decodeEvent);
  },
  /** The recording's screen, an asciicast v2, as text (byj-1wv.5.7). */
  async recordingScreen(id: string): Promise<string> {
    const res = await fetch(`/v1/recordings/${enc(id)}/screen`, { credentials: "same-origin" });
    const text = await res.text();
    if (!res.ok) parse<unknown>(res, text);
    return text;
  },
  createRecordingInvite: (id: string, max_uses: number, ttl_s: number) =>
    call<Invite>("POST", `/v1/recordings/${enc(id)}/invites`, { max_uses, ttl_s }),
  deleteRecording: (id: string) => call<void>("DELETE", `/v1/recordings/${enc(id)}`),
  /** Mail people to review several recordings (byj-1wv.44). */
  requestReviews: (recordings: string[], emails: string[], note: string) =>
    call<{ results: ReviewRequestResult[] }>("POST", "/v1/recordings/review-requests", { recordings, emails, note }),
  annotations: (id: string) => call<Annotation[]>("GET", `/v1/recordings/${enc(id)}/annotations`),
  annotate: (id: string, a: { seq_ref: number; anchor: string; body: string; parent_id?: string }) =>
    call<Annotation>("POST", `/v1/recordings/${enc(id)}/annotations`, a),
  setResolved: (id: string, aid: string, resolved: boolean) =>
    call<Annotation>("PATCH", `/v1/recordings/${enc(id)}/annotations/${enc(aid)}`, { resolved }),
  reviewDone: (id: string) => call<void>("POST", `/v1/recordings/${enc(id)}/review-done`),

  classes: () => call<ClassSummary[]>("GET", "/v1/classes"),
  createClass: (name: string, policy: ClassPolicy, assignments: AssignmentIn[] = []) =>
    call<{ id: string; name: string; join_code: string; assignments: Assignment[] }>("POST", "/v1/classes", { name, policy, assignments }),
  addAssignment: (id: string, a: AssignmentIn) => call<Assignment>("POST", `/v1/classes/${enc(id)}/assignments`, a),
  /** Title, text and due date; `clear_due` drops the due date. */
  updateAssignment: (id: string, key: string, patch: { title?: string; text?: string; due_at?: string; clear_due?: boolean }) =>
    call<Assignment>("PATCH", `/v1/classes/${enc(id)}/assignments/${enc(key)}`, patch),
  archiveAssignment: (id: string, key: string) => call<void>("DELETE", `/v1/classes/${enc(id)}/assignments/${enc(key)}`),
  assignmentPage: (id: string, key: string) => call<AssignmentPage>("GET", `/v1/classes/${enc(id)}/assignments/${enc(key)}`),
  /** The class's recordings, its instructor's, in one request (byj-j98). */
  classRecordings: (id: string) => call<ClassRecordingList>("GET", `/v1/classes/${enc(id)}/recordings`),
  /** A class share's assignment, set by its owner or the class's instructor; null clears it. */
  setShareAssignment: (share: string, key: string | null) =>
    call<ShareDetail>("PATCH", `/v1/shares/${enc(share)}`, key === null ? { clear: true } : { assignment: key }),
  class: (id: string) => call<ClassDetail>("GET", `/v1/classes/${enc(id)}`),
  updateClassPolicy: (id: string, policy: ClassPolicy) =>
    call<ClassDetail>("PATCH", `/v1/classes/${enc(id)}`, { policy }),
  /** The class, its roster, assignments and exam assignments; its students' shares and recordings stay (byj-56x). */
  deleteClass: (id: string) => call<void>("DELETE", `/v1/classes/${enc(id)}`),
  removeMember: (id: string, user: string) =>
    call<void>("DELETE", `/v1/classes/${enc(id)}/members/${enc(user)}`),
  joinPreview: (code: string) => call<JoinPreview>("GET", `/v1/classes/join/${enc(code)}`),
  joinClass: (join_code: string, consent_version: string) =>
    call<{ class: { id: string; name: string }; role: "student" }>("POST", "/v1/classes/join", {
      join_code,
      consent_version,
    }),
  classSessions: (id: string) => call<ClassSession[]>("GET", `/v1/classes/${enc(id)}/sessions`),
  broadcast: (id: string, text: string) =>
    call<{ delivered: number }>("POST", `/v1/classes/${enc(id)}/broadcast`, { text }),
  /** Posted to the class's page, and left in each live session as a broadcast is (byj-1wv.135). */
  announce: (id: string, text: string) =>
    call<{ announcement: Announcement; delivered: number }>("POST", `/v1/classes/${enc(id)}/announcements`, { text }),
  deleteAnnouncement: (id: string, ann: string) => call<void>("DELETE", `/v1/classes/${enc(id)}/announcements/${enc(ann)}`),

  /** My Share-hosted machines (RFC-050); 404 when the relay hosts none. */
  machines: () => call<MachinesView>("GET", "/v1/machines"),
  /** A machine on my own computer: the image to run, and mine running (byj-1wv.62.23). */
  ownMachines: () => call<OwnMachines>("GET", "/v1/machines/own"),
  // plans and payments (RFC-065)
  plans: () => call<PlansView>("GET", "/v1/billing/plans"),
  billing: () => call<BillingView>("GET", "/v1/billing"),
  /** a class plan's checkout names its class, its seats and its term's end (RFC-065 D7) */
  checkout: (plan: string, pay: "card" | "once", seats?: { class_id: string; seats: number; ends_at: string }) =>
    call<Checkout>("POST", "/v1/billing/checkout", { plan, pay, ...seats }),
  confirmPayment: (orderId: string, paymentKey: string, amount: number) =>
    call<Settled>("POST", "/v1/billing/confirm", { order_id: orderId, payment_key: paymentKey, amount }),
  registerCard: (orderId: string, authKey: string, customerKey: string) =>
    call<Settled>("POST", "/v1/billing/card", { order_id: orderId, auth_key: authKey, customer_key: customerKey }),
  cancelPlan: () => call<{ refunded: boolean; amount?: number; currency?: string; subscription?: Subscription }>("POST", "/v1/billing/cancel"),
  resumePlan: () => call<{ subscription: Subscription }>("POST", "/v1/billing/resume"),
  adminBilling: () => call<AdminBilling>("GET", "/v1/admin/billing"),
  adminRefund: (orderId: string) => call<BillingOrder>("POST", `/v1/admin/billing/payments/${enc(orderId)}/refund`),
  /** With an exam assignment, that exam's machine, on a fresh home of its own (byj-1wv.62.10). */
  startMachine: (exam?: string) => call<MachineView>("POST", "/v1/machines", exam ? { exam } : undefined),
  /** An author's machine, its home theirs for authoring (RFC-063). */
  startAuthorMachine: () => call<MachineView>("POST", "/v1/machines", { author: true }),
  /** A token for a machine on my own computer, a machine's device (RFC-063 D5); said once. */
  ownMachineToken: (name?: string) => call<OwnMachineToken>("POST", "/v1/machines/own/token", name ? { name } : {}),
  /** The papers I submitted as an author, as the bank says them (RFC-063). */
  authorPapers: async () => (await call<{ papers: AuthorPaper[] }>("GET", "/v1/exam-authoring/papers")).papers,
  /** My month at the model gateway as an author (RFC-063 D12). */
  authorModel: () => call<AuthorModel>("GET", "/v1/exam-authoring/model"),
  stopMachine: (id: string) => call<void>("DELETE", `/v1/machines/${enc(id)}`),
  /** An admin's (RFC-050 "By grant"): people found by email or name, grants, granting and revoking. */
  adminUsers: async (q: string) => (await call<{ users: AdminUser[] }>("GET", `/v1/admin/users?q=${enc(q)}`)).users,
  adminGrants: async (addon = "machines") =>
    (await call<{ entitlements: Grant[] }>("GET", `/v1/admin/entitlements?addon=${enc(addon)}`)).entitlements,
  grant: (g: GrantRequest) => call<Grant>("POST", "/v1/admin/entitlements", { addon: "machines", ...g }),
  revoke: (userId: string, addon = "machines", source?: GrantSource) =>
    call<void>("DELETE", `/v1/admin/entitlements/${enc(userId)}/${enc(addon)}${source && source !== "admin" ? `?source=${enc(source)}` : ""}`),
  /** What an agent share was asked, its owner's to read; a person's own ratings of its answers, and one rated (byj-1wv.48). */
  agentUse: (shareId: string) => call<AgentUse>("GET", `/v1/shares/${enc(shareId)}/agent-use`),
  myRatings: async (shareId: string) => (await call<{ ratings: Record<string, number> }>("GET", `/v1/shares/${enc(shareId)}/ratings`)).ratings,
  rateAnswer: (shareId: string, seq: number, rating: number) =>
    call<{ rating: number }>("PUT", `/v1/shares/${enc(shareId)}/exchanges/${seq}/rating`, { rating }),
  /** Which agents owners share, by reach, over the last `days` or since the first (byj-1wv.49). */
  adminAgentShares: (days?: number) => call<AgentShareReport>("GET", `/v1/admin/agent-shares${days ? `?days=${days}` : ""}`),
  /** Machine homes and how long each is kept (byj-1wv.62.19): the relay's days, a person's set (null the relay's, 0 always), a home removed now. */
  adminHomes: async () => {
    const r = await call<{ homes: MachineHome[]; default_retain_days: number }>("GET", "/v1/admin/machine-homes");
    return { homes: r.homes, defaultDays: r.default_retain_days };
  },
  setHomeRetention: (userId: string, days: number | null) =>
    call<void>("PUT", `/v1/admin/machine-homes/${enc(userId)}`, { retain_days: days }),
  removeHome: (userId: string, exam?: string) =>
    call<void>("DELETE", `/v1/admin/machine-homes/${enc(userId)}${exam ? `?exam=${enc(exam)}` : ""}`),
  /** People's files (RFC-054 §6): what each person's take of their quota, a person's quota set (MB; null the relay's), their files, one removed. */
  adminFiles: async () => {
    const r = await call<{ people: FileUse[]; default_quota: number; month: string; downloaded: number; downloaded_last: number }>("GET", "/v1/admin/files");
    return { people: r.people, defaultQuota: r.default_quota, month: r.month, downloaded: r.downloaded, downloadedLast: r.downloaded_last };
  },
  /** Who is an admin, and admin delegated for a time or ended (byj-1wv.72): a configured admin's alone to give or end. */
  adminAdmins: () => call<AdminsList>("GET", "/v1/admin/admins"),
  delegateAdmin: (email: string, expiresAt: string) =>
    call<{ delegate: AdminDelegate; mailed: number }>("POST", "/v1/admin/admins", { email, expires_at: expiresAt }),
  endAdminDelegation: (userId: string) => call<void>("DELETE", `/v1/admin/admins/${enc(userId)}`),
  /** The files' storage settings (byj-1wv.71.2): the default quota, the total, the prices. */
  fileSettings: () => call<FileSettings>("GET", "/v1/admin/file-settings"),
  setFileSettings: (change: FileSettingsChange) => call<{ settings: FileLimits }>("PUT", "/v1/admin/file-settings", change),
  adminFilesOf: async (userId: string) => (await call<{ files: ShareFile[] }>("GET", `/v1/admin/files/${enc(userId)}`)).files,
  setFileQuota: (userId: string, mb: number | null) => call<void>("PUT", `/v1/admin/files/${enc(userId)}`, { quota_mb: mb }),
  adminRemoveFile: (userId: string, fileId: string) => call<void>("DELETE", `/v1/admin/files/${enc(userId)}/${enc(fileId)}`),
  exams: () => call<{ teaching: ExamSummary[]; assigned: ExamAssignmentView[] }>("GET", "/v1/exams"),
  /** The sessions the person runs and how each stands, for the exam console (RFC-062 D9). */
  consoleSessions: () => call<{ sessions: ConsoleSessionSummary[]; now: string }>("GET", "/v1/exam-console/sessions"),
  /** A session the person runs, its exam and each candidate as they stand (RFC-062 D9). */
  consoleSession: (classId: string) => call<ConsoleSession>("GET", `/v1/exam-console/sessions/${encodeURIComponent(classId)}`),
  /** A new exam, to write on the page and pull into a kit (byj-1wv.56.11). */
  createExam: (title: string) => call<{ id: string; title: string }>("POST", "/v1/exams", { title }),
  exam: (id: string) => call<ExamDetail>("GET", `/v1/exams/${enc(id)}`),
  /** The exam withdrawn from a class, or deleted with every assignment (byj-1wv.56.14): their hand-ins and grades go. */
  withdrawExam: (assignmentId: string) => call<void>("DELETE", `/v1/exam-assignments/${enc(assignmentId)}`),
  deleteExam: (id: string) => call<void>("DELETE", `/v1/exams/${enc(id)}`),
  /** The exams assigned to a class, for its instructor (byj-1wv.56.15). */
  classExams: (classId: string) => call<ClassExam[]>("GET", `/v1/classes/${enc(classId)}/exams`),
  examAssignment: (id: string) => call<ExamAssignmentPage>("GET", `/v1/exam-assignments/${enc(id)}`),
  examSubmissions: (id: string) => call<ExamSubmission[]>("GET", `/v1/exam-assignments/${enc(id)}/submissions`),
  /** Override a grade's score with a note, release it to its student, or hold it back. */
  patchGrade: (id: string, patch: { override?: number; clear_override?: boolean; note?: string; released?: boolean }) =>
    call<ExamGrade>("PATCH", `/v1/exam-grades/${enc(id)}`, patch),
  /** The instructor's bank: their items, with their exposure (RFC-055). */
  examBank: () => call<{ items: BankItem[] }>("GET", "/v1/exam-bank"),
  /** A :choice question answered on the page, handed in by the relay (RFC-055). */
  answerChoice: (assignmentId: string, question: string, answers: number[]) => call<ExamSubmission>("POST", `/v1/exam-assignments/${enc(assignmentId)}/answers`, { question, answers }),
  /** An assignment's review (RFC-055): what is reviewed, the totals, what stops its grades being finished. */
  examReview: (id: string) => call<ReviewState>("GET", `/v1/exam-assignments/${enc(id)}/review`),
  setExamReview: (id: string, s: Partial<Pick<ReviewSettings, "review" | "pass_mark" | "sample" | "review_gap">>) => call<ReviewSettings>("PATCH", `/v1/exam-assignments/${enc(id)}/review`, s),
  addExamReviewer: (id: string, email: string) => call<{ id: string; name?: string; email?: string }>("POST", `/v1/exam-assignments/${enc(id)}/reviewers`, { email }),
  removeExamReviewer: (id: string, user: string) => call<void>("DELETE", `/v1/exam-assignments/${enc(id)}/reviewers/${enc(user)}`),
  /** A review; `parts.labels` its reviewer's verdict on each judged fluency signal, which the judge is set against (RFC-055). */
  reviewGrade: (gradeId: string, r: { score: number; reason: string; note?: string; parts?: { labels?: Record<string, boolean> } }) =>
    call<ExamReview>("POST", `/v1/exam-grades/${enc(gradeId)}/reviews`, r),
  finalizeExam: (id: string) => call<{ settings: ReviewSettings }>("POST", `/v1/exam-assignments/${enc(id)}/finalize`, {}),
  reopenExam: (id: string, reason: string) => call<ReviewSettings>("POST", `/v1/exam-assignments/${enc(id)}/reopen`, { reason }),
  examAudit: (id: string) => call<GradeEvent[]>("GET", `/v1/exam-assignments/${enc(id)}/audit`),
  /** An exam's draft, or null when it has none. */
  async examDraft(id: string): Promise<ExamDraft | null> {
    try {
      return await call<ExamDraft>("GET", `/v1/exams/${enc(id)}/draft`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  putExamDraft: (id: string, body: ExamDraftBody) => call<ExamDraft>("PUT", `/v1/exams/${enc(id)}/draft`, { body }),
  /** The app templates I own and those shared with me (RFC-052). */
  appTemplates: () => call<{ mine: AppTemplate[]; shared: AppTemplate[] }>("GET", "/v1/app-templates"),
  // the agent builder (RFC-060)
  agents: () => call<{ agents: BuiltAgent[]; given: AppTemplate[] }>("GET", "/v1/agents"),
  putAgent: (name: string, body: { share_id?: string; shared?: boolean; test?: AgentTestSummary }) =>
    call<{ name: string; share_id: string; shared: boolean }>("PUT", `/v1/agents/${encodeURIComponent(name)}`, body),
  deleteAgent: (name: string) => call<void>("DELETE", `/v1/agents/${encodeURIComponent(name)}`),
  shareApps: (share: string) => call<{ apps: ShareApp[] }>("GET", `/v1/shares/${encodeURIComponent(share)}/apps`),
  newShareApp: (share: string, name: string) => call<ShareAppToken>("POST", `/v1/shares/${encodeURIComponent(share)}/apps`, { name }),
  shareAppToken: (share: string, app: string) =>
    call<ShareAppToken>("POST", `/v1/shares/${encodeURIComponent(share)}/apps/${encodeURIComponent(app)}/token`, {}),
  removeShareApp: (share: string, app: string) => call<void>("DELETE", `/v1/shares/${encodeURIComponent(share)}/apps/${encodeURIComponent(app)}`),
  appTemplate: (id: string) => call<AppTemplateDetail>("GET", `/v1/app-templates/${enc(id)}`),
  /** Its owner's: a field left out is kept. */
  describeAppTemplate: (id: string, patch: { title?: string; description?: string }) =>
    call<AppTemplate>("PATCH", `/v1/app-templates/${enc(id)}`, patch),
  /** Its versions and their archives; apps made from it are copies and stay. */
  deleteAppTemplate: (id: string) => call<void>("DELETE", `/v1/app-templates/${enc(id)}`),
  /** A link that makes whoever accepts it a user of the template; max_uses 0 is anyone with it. */
  createAppTemplateInvite: (id: string, max_uses: number, ttl_s: number) =>
    call<Invite & { id: string; role: string; max_uses: number }>("POST", `/v1/app-templates/${enc(id)}/invites`, { max_uses, ttl_s }),
  /** To a class its owner teaches, by id or join code. */
  giveAppTemplate: (id: string, cls: string) =>
    call<{ class: string; name: string; code: string }>("POST", `/v1/app-templates/${enc(id)}/classes`, { class: cls }),
  takeAppTemplate: (id: string, cls: string) => call<void>("DELETE", `/v1/app-templates/${enc(id)}/classes/${enc(cls)}`),
  /** An invited user no longer may use it. */
  removeAppTemplateUser: (id: string, user: string) => call<void>("DELETE", `/v1/app-templates/${enc(id)}/users/${enc(user)}`),
  /** The files I own and those shared with me, with what mine take of my quota (RFC-054). */
  files: () => call<{ mine: ShareFile[]; shared: ShareFile[]; used: number; quota: number }>("GET", "/v1/files"),
  file: (id: string) => call<ShareFileDetail>("GET", `/v1/files/${enc(id)}`),
  /** A new file; `assignment`, CODE/KEY, hands it in under a class's assignment. */
  createFile: (name: string, kind: "file" | "folder", description = "", assignment?: string) =>
    call<ShareFile>("POST", "/v1/files", { name, kind, description, ...(assignment ? { assignment } : {}) }),
  /** The files given to a class, for its members (RFC-054 §5). */
  classFiles: (id: string) => call<{ files: ShareFile[] }>("GET", `/v1/classes/${enc(id)}/files`).then((r) => r.files),
  /** The app templates given to a class, for its members, newest updated first (RFC-052 §5, byj-1wv.77). */
  classAppTemplates: (id: string) =>
    call<{ templates: AppTemplate[] }>("GET", `/v1/classes/${enc(id)}/app-templates`).then((r) => r.templates),
  /** Its owner's: a field left out is kept. */
  describeFile: (id: string, patch: { name?: string; description?: string }) => call<ShareFile>("PATCH", `/v1/files/${enc(id)}`, patch),
  /** Its versions and their bytes. */
  deleteFile: (id: string) => call<void>("DELETE", `/v1/files/${enc(id)}`),
  deleteFileVersion: (id: string, n: number) => call<void>("DELETE", `/v1/files/${enc(id)}/versions/${n}`),
  /**
   * Bytes as the file's next version: declared by their size and hash, then
   * put once to the URL the relay hands back — on this origin, whatever the
   * relay calls itself.
   */
  async uploadFile(id: string, body: ArrayBuffer): Promise<number> {
    const v = await call<{ version: number; upload_url: string }>("POST", `/v1/files/${enc(id)}/versions`, {
      bytes: body.byteLength,
      hash: await sha256Hex(body),
    });
    const u = new URL(v.upload_url, "http://relay.invalid");
    const res = await fetch(u.pathname + u.search, { method: "PUT", body, headers: { "Content-Type": "application/octet-stream" } });
    parse<unknown>(res, await res.text());
    return v.version;
  },
  folderEntries: (id: string, n: number) => call<{ entries: FolderEntry[] }>("GET", `/v1/files/${enc(id)}/versions/${n}/entries`),
  /** A link that makes whoever accepts it, signed in, a viewer; max_uses 0 is anyone with it. */
  createFileInvite: (id: string, max_uses: number, ttl_s: number) =>
    call<Invite & { id: string; role: string; max_uses: number }>("POST", `/v1/files/${enc(id)}/invites`, { max_uses, ttl_s }),
  addFilePeople: (id: string, emails: string[], note: string) =>
    call<{ results: FilePersonResult[] }>("POST", `/v1/files/${enc(id)}/people`, { emails, note }),
  removeFileViewer: (id: string, user: string) => call<void>("DELETE", `/v1/files/${enc(id)}/viewers/${enc(user)}`),
  fileComments: (id: string) => call<{ comments: FileComment[] }>("GET", `/v1/files/${enc(id)}/comments`).then((r) => r.comments),
  /** A hand-in marked checked or returned by its class's instructor, a mark beside it if given (byj-1wv.136). */
  reviewHandIn: (id: string, state: HandInReview["state"], mark: string) => call<HandInReview>("PUT", `/v1/files/${enc(id)}/review`, { state, mark }),
  clearHandInReview: (id: string) => call<void>("DELETE", `/v1/files/${enc(id)}/review`),
  /** A new thread on a line, or with parent_id a reply in one (its place is the thread's). */
  commentOnFile: (id: string, c: { version?: number; path?: string; line?: number; body: string; parent_id?: string }) =>
    call<FileComment>("POST", `/v1/files/${enc(id)}/comments`, c),
  resolveFileComment: (id: string, comment: string, resolved: boolean) =>
    call<FileComment>("PATCH", `/v1/files/${enc(id)}/comments/${enc(comment)}`, { resolved }),
  /** To a class its owner teaches, by id or join code. */
  giveFile: (id: string, cls: string) => call<{ class: string; name: string; code: string }>("POST", `/v1/files/${enc(id)}/classes`, { class: cls }),
  takeFile: (id: string, cls: string) => call<void>("DELETE", `/v1/files/${enc(id)}/classes/${enc(cls)}`),
  /** A student disputes their released grade, saying why. */
  dispute: (gradeId: string, text: string) => call<ExamDispute>("POST", `/v1/exam-grades/${enc(gradeId)}/disputes`, { text }),
  /** The instructor answers a dispute, once. */
  answerDispute: (id: string, answer: string) => call<ExamDispute>("PATCH", `/v1/exam-disputes/${enc(id)}`, { answer }),
};

/** Where an exam's grades, and a class's exams' grades, are exported as CSV (byj-1wv.56.6). */
export const examExportURL = (id: string) => `/v1/exams/${enc(id)}/export.csv`;
export const classExamsExportURL = (id: string) => `/v1/classes/${enc(id)}/exams.csv`;
/**
 * A file's version (a number, or "latest"), as the relay serves it (RFC-054
 * §3): inline when it is text, an image or a PDF, else — or with download —
 * an attachment; the cookie is the auth.
 */
export const fileContentURL = (id: string, n: number | "latest", download = false) =>
  `/v1/files/${enc(id)}/versions/${n}/content${download ? "?download=1" : ""}`;
/** One file of a folder's version, by its path in it. */
export const folderEntryURL = (id: string, n: number, path: string, download = false) =>
  `/v1/files/${enc(id)}/versions/${n}/entry?path=${enc(path)}${download ? "&download=1" : ""}`;
/** Where a template's version is downloaded, as the tar.gz byj made (RFC-052); the cookie is the auth. */
export const appTemplateArchiveURL = (id: string, n: number) => `/v1/app-templates/${enc(id)}/versions/${n}/archive`;

/**
 * A new class's policy until its instructor changes it (byj-1wv.4.7, byj-1wv.4.8): the instructor may request
 * turns, each approved by the student at their terminal; recordings kept 30 days.
 */
export const DefaultClassPolicy: ClassPolicy = {
  inject: true,
  consent_inject: false,
  retention_days: 30,
  alerts: { permission_wait_s: 120, cost_spike_x: 5, idle_min: 15 },
};

/** The API calls the review view makes, so a test can supply its own. */
export type ReviewApi = Pick<
  typeof api,
  | "recording"
  | "recordingEvents"
  | "recordingScreen"
  | "annotations"
  | "annotate"
  | "setResolved"
  | "reviewDone"
  | "createRecordingInvite"
  | "deleteRecording"
  | "updateRecording"
>;

/** A WebSocket URL on this origin. */
export function wsURL(path: string, loc: Pick<Location, "protocol" | "host"> = window.location): string {
  return `${loc.protocol === "https:" ? "wss:" : "ws:"}//${loc.host}${path}`;
}

/** The subprotocol a live socket offers beside its ticket (RFC-059 D3). */
export const TicketProtocol = "byj.v1";

/**
 * What a live socket's URL opens: a share's frames, screen or workbench
 * (/v1/shares/{id}/…), or a class's feed (/v1/classes/{id}/ws); null for
 * any other.
 */
export function liveScope(url: string): { share: string } | { class: string } | null {
  const m = /\/v1\/(shares|classes)\/([^/?#]+)\//.exec(url);
  if (!m?.[2]) return null;
  const id = decodeURIComponent(m[2]);
  return m[1] === "shares" ? { share: id } : { class: id };
}

/**
 * A live socket at url, opened with the ticket the main server signs for
 * the share or class it names (RFC-059 D3): the relay keeps no account and
 * reads no cookie. Rejected when no ticket is given, which the caller takes
 * as a connection lost.
 */
export async function openLive(url: string): Promise<WebSocket> {
  const scope = liveScope(url);
  if (!scope) throw new Error(`not a live socket: ${url}`);
  const { ticket } = await call<{ ticket: string }>("POST", "/v1/live-tickets", scope);
  return new WebSocket(url, [TicketProtocol, ticket]);
}
